import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MeetingHomeItemResponse,
  MeetingsHomeResponse,
} from '@interfaces/meetings/responses/meetings-home.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { SharedModule } from '@shared/shared.module';
import { catchError, map, of, Subject, switchMap, tap } from 'rxjs';
import {
  calendarDaysBetween,
  formatDuration,
  formatParticipants,
  hiddenItemsLabel,
  joinMeetingParts,
  monthAbbreviation,
  relativeDayLabel,
  truncateList,
  weekdayAbbreviation,
} from './meetings-display.util';

/** Itens por bloco do painel (crítica 3819, G2): o resto vira "+ N" para não esticar o par Limites + Reuniões. */
const PANEL_ITEMS_LIMIT = 3;

type LoadResult = { ok: true; data: MeetingsHomeResponse | null } | { ok: false };

interface NextMeetingView {
  id: string;
  title: string;
  month: string;
  day: string;
  meta: string;
  /** "Hoje", "Amanhã", "Em N dias" (crítica 3819, A5). */
  relativeDay: string | null;
  meetingUrl: string | null;
}

interface PanelListView {
  all: string[];
  visible: string[];
  hiddenLabel: string | null;
}

interface RecentMeetingView {
  id: string;
  title: string;
  meta: string;
  summary: string | null;
  decisions: PanelListView;
  nextSteps: PanelListView;
  hasInsights: boolean;
}

/**
 * Card "Reuniões" da Home (card 3786): a próxima reunião entre a Legatus e a empresa e as 3 últimas
 * concluídas, com resumo, decisões e próximos passos do Cog. Só leitura (decisão P-02): arquivar e
 * excluir ficam na tela Meetings. Dados: `GET meetings/home?companyId=`.
 *
 * Quem decide se o card aparece é a Home (`MEETING_VIEW_COMPANY` ou `MEETING_LIST`). Os links para a
 * tela Meetings (abrir e Ver Todas) só aparecem com `canOpenMeetings` (`MEETING_LIST`), que o
 * cliente não tem. O "Agendar" do protótipo está pendente: não há fluxo de agendamento.
 */
@Component({
  selector: 'app-home-meetings-card',
  standalone: true,
  // NgTemplateOutlet explícito: o SharedModule não reexporta o CommonModule, e sem ele o `*ngTemplateOutlet` do
  // painel não renderizava nada, em silêncio (decisões e próximos passos sumiam; crítica 3819 r2, N1).
  imports: [SharedModule, RouterLink, NgTemplateOutlet],
  templateUrl: './home-meetings-card.component.html',
  styleUrls: ['./home-meetings-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeMeetingsCardComponent {
  private readonly http = inject(MeetingsHttpService);
  private readonly dateFormatter = inject(DateFormatterService);

  /** Empresa escolhida no filtro da Home; `null` com nenhuma ou várias empresas. */
  readonly companyId = input<string | null>(null);
  /** Muda quando a pessoa clica em Atualizar no cabeçalho da Home. */
  readonly refreshKey = input(0);
  /** `MEETING_LIST`: pode abrir a tela Meetings (time interno). */
  readonly canOpenMeetings = input(false);

  readonly loading = signal(false);
  readonly failed = signal(false);
  readonly meetings = signal<MeetingsHomeResponse | null>(null);
  /** Reunião recente com o painel de resumo aberto. */
  readonly expandedId = signal<string | null>(null);
  /**
   * Reunião com as listas inteiras no painel. Só para quem não abre a tela Meetings (cliente): o time interno vai
   * para `/dashboard/meetings/:id` pelo "+ N". O painel tem altura máxima com rolagem, então o par não estica.
   */
  readonly fullListsId = signal<string | null>(null);

  readonly meetingsRoute = ['/dashboard/meetings'];

  /** Sem próxima e sem recentes: um vazio único que explica de onde vêm as reuniões (crítica 3819, G5). */
  readonly isEmpty = computed(() => !this.next() && this.recent().length === 0);

  readonly next = computed<NextMeetingView | null>(() => {
    const next = this.meetings()?.next;
    return next ? this.toNextView(next) : null;
  });

  readonly recent = computed<RecentMeetingView[]>(() =>
    (this.meetings()?.recent ?? []).slice(0, 3).map(item => this.toRecentView(item))
  );

  private readonly requests$ = new Subject<string>();

  constructor() {
    this.requests$
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.failed.set(false);
        }),
        switchMap(companyId =>
          this.http.getHome(companyId).pipe(
            map((data): LoadResult => ({ ok: true, data })),
            catchError(() => of<LoadResult>({ ok: false }))
          )
        ),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe(result => {
        this.loading.set(false);
        if (!result.ok || !result.data) {
          this.failed.set(true);
          this.meetings.set(null);
          return;
        }
        this.meetings.set(result.data);
        this.fullListsId.set(null);
        // Como no protótipo: a mais recente já abre com o resumo, quando ele existe.
        const first = result.data.recent?.[0];
        this.expandedId.set(first && this.hasInsights(first) ? first.id : null);
      });

    effect(() => {
      const companyId = this.companyId();
      this.refreshKey();
      untracked(() => {
        if (companyId) {
          this.requests$.next(companyId);
          return;
        }
        this.meetings.set(null);
        this.failed.set(false);
        this.loading.set(false);
      });
    });
  }

  retry(): void {
    const companyId = this.companyId();
    if (companyId) {
      this.requests$.next(companyId);
    }
  }

  toggle(meetingId: string): void {
    this.expandedId.update(current => (current === meetingId ? null : meetingId));
    this.fullListsId.set(null);
  }

  showFullLists(meetingId: string): void {
    this.fullListsId.set(meetingId);
  }

  private toNextView(item: MeetingHomeItemResponse): NextMeetingView {
    const start = item.scheduledStartAt;
    const weekday = weekdayAbbreviation(Number(this.dateFormatter.formatDateInFormat(start, 'i')));
    const time = this.dateFormatter.formatDateInFormat(start, 'HH:mm');

    return {
      id: item.id,
      title: item.title,
      month: monthAbbreviation(Number(this.dateFormatter.formatDateInFormat(start, 'M'))),
      day: this.dateFormatter.formatDateInFormat(start, 'dd'),
      meta: joinMeetingParts([
        weekday ? `${weekday}, ${time}` : time,
        formatDuration(item.durationMinutes),
        item.platform,
      ]),
      relativeDay: relativeDayLabel(
        calendarDaysBetween(
          this.dateFormatter.formatDateInFormat(this.dateFormatter.getNow(), 'yyyy-MM-dd'),
          this.dateFormatter.formatDateInFormat(start, 'yyyy-MM-dd')
        )
      ),
      meetingUrl: item.meetingUrl,
    };
  }

  private toRecentView(item: MeetingHomeItemResponse): RecentMeetingView {
    return {
      id: item.id,
      title: item.title,
      meta: joinMeetingParts([
        this.dateFormatter.formatDateInFormat(item.scheduledStartAt, 'dd/MM/yyyy'),
        formatDuration(item.durationMinutes),
        formatParticipants(item.participantsCount),
      ]),
      summary: item.summary,
      decisions: this.toPanelList(item.decisions, 'decisão', 'decisões'),
      nextSteps: this.toPanelList(item.nextSteps, 'próximo passo', 'próximos passos'),
      hasInsights: this.hasInsights(item),
    };
  }

  private toPanelList(
    items: string[] | null | undefined,
    singular: string,
    plural: string
  ): PanelListView {
    const { visible, hiddenCount } = truncateList(items, PANEL_ITEMS_LIMIT);
    return {
      all: items ?? [],
      visible,
      hiddenLabel: hiddenItemsLabel(hiddenCount, singular, plural),
    };
  }

  private hasInsights(item: MeetingHomeItemResponse): boolean {
    return !!item.summary || (item.decisions?.length ?? 0) > 0 || (item.nextSteps?.length ?? 0) > 0;
  }
}
